import * as React from "react";

import { Button } from "@blueprintjs/core";
import styled from "~/styled";

interface IProps {
  src: string | null;
  onCommit: (image: File) => any;
}

interface IState {
  x: number;
  y: number;
  mouseDown: boolean;
  overwriteColor: string;
  overwriteColorPickerActive: boolean;
}

const ColorPreview = styled.span<{ color: string }>`
  display: inline-block;
  height: 16px;
  width: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.5);
  background-color: ${props => props.color};
`;

const Wrapper = styled.div`
  > canvas {
    cursor: crosshair;
    height: auto;
    object-fit: contain;
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.4);
    border-radius: 3px;
  }
`;

const Actions = styled.div`
  grid-area: actions;
  display: grid;
  grid-auto-flow: column;
  gap: 6px;
`;

const initialState: IState = {
  mouseDown: false,
  overwriteColor: "rgba(255,255,255,1)",
  overwriteColorPickerActive: false,
  x: 0,
  y: 0
};

export default class ImageCanvas extends React.Component<IProps, IState> {
  public readonly state: IState = initialState;

  private canvas: HTMLCanvasElement | null = null;

  public render() {
    const { overwriteColorPickerActive, overwriteColor } = this.state;

    if (this.props.src === null) {
      return null;
    }

    return (
      <Wrapper>
        <canvas
          ref={ref => (this.canvas = ref)}
          onContextMenu={this.handleChooseColor}
          onMouseDown={
            overwriteColorPickerActive
              ? this.handleChooseColor
              : this.handleMouseDown
          }
          onMouseUp={this.handleMouseUp}
          onMouseMove={this.handleMouseMove}
        />

        <Actions>
          <Button
            active={overwriteColorPickerActive}
            icon={<ColorPreview color={overwriteColor} />}
            onClick={this.handleOverwriteColorClick}
          >
            Pick overwrite color
          </Button>

          <Button intent="danger" onClick={this.handleResetClick}>
            Reset
          </Button>
        </Actions>
      </Wrapper>
    );
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    if (prevProps.src !== this.props.src) {
      this.loadFromSource();
      this.setState(initialState);
    }
  }

  public async componentDidMount() {
    this.loadFromSource();
  }

  private handleResetClick = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    this.loadFromSource();
  };

  private handleOverwriteColorClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();

    this.setState(state => ({
      overwriteColorPickerActive: !state.overwriteColorPickerActive
    }));
  };

  private handleChooseColor = (event: React.MouseEvent<HTMLCanvasElement>) => {
    event.preventDefault();

    const { x, y } = this.pointFromEvent(event);
    const ctx = this.getContext();
    const color = ctx.getImageData(x, y, 1, 1).data.slice(0, 4);

    this.setState({
      mouseDown: false,
      overwriteColor: `rgba(${color.join(",")})`,
      overwriteColorPickerActive: false
    });
  };

  private handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    event.preventDefault();

    const { x, y } = this.pointFromEvent(event);

    this.setState({
      mouseDown: true,
      x,
      y
    });
  };

  private handleMouseUp = (event: React.MouseEvent<HTMLCanvasElement>) => {
    this.setState({ mouseDown: false });
    this.commit();
  };

  private handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!this.state.mouseDown) {
      return;
    }

    const { x, y } = this.pointFromEvent(event);

    const ctx = this.getContext();
    const canvas = this.getCanvas();

    ctx.strokeStyle = this.state.overwriteColor;
    ctx.lineWidth = canvas.height / 15;
    ctx.lineCap = "round";
    ctx.globalCompositeOperation = "source-over";

    ctx.beginPath();
    ctx.moveTo(this.state.x, this.state.y);
    ctx.lineTo(x, y);
    ctx.stroke();

    this.setState({ x, y });
  };

  private renderImage(image: HTMLImageElement) {
    const canvas = this.getCanvas();
    const ctx = this.getContext();

    canvas.height = image.naturalHeight;
    canvas.width = image.naturalWidth;

    ctx.drawImage(image, 0, 0);
  }

  private getCanvas(): HTMLCanvasElement {
    if (!this.canvas) {
      throw new Error("Canvas is unavailable");
    }

    return this.canvas;
  }

  private getContext(): CanvasRenderingContext2D {
    const ctx = this.getCanvas().getContext("2d");

    if (!ctx) {
      throw new Error("Canvas context is not available");
    }

    return ctx;
  }

  private commit = () => {
    this.getCanvas().toBlob(blob => {
      if (!blob) {
        throw new Error("Unable to create canvas blob");
      }

      const filename = (this.props.src || "").split("/").pop();
      const file = new File([blob], filename || "test.png", {
        type: "image/png"
      });

      this.props.onCommit(file);
    });
  };

  private async loadFromSource() {
    if (!this.props.src) {
      return;
    }
    const image = await this.loadImage(this.props.src);
    this.renderImage(image);
    this.commit();
  }

  private async loadImage(url: string): Promise<HTMLImageElement> {
    const image = new Image();
    image.src = url + `?canvas=${+new Date()}`;
    image.crossOrigin = "anonymous";

    return new Promise<HTMLImageElement>((resolve, reject) => {
      image.onload = () => resolve(image);
      image.onerror = () => reject();
    });
  }

  private pointFromEvent(event: React.MouseEvent<HTMLElement>) {
    const canvas = this.getCanvas();
    const x =
      (event.pageX - event.currentTarget.offsetLeft) *
      (canvas.height / canvas.clientHeight);
    const y =
      (event.pageY - event.currentTarget.offsetTop) *
      (canvas.width / canvas.clientWidth);

    return { x, y };
  }
}



// WEBPACK FOOTER //
// ./src/components/Remoderation/ImageCanvas.tsx